import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EVENTO_AGUARDANDO,
  EVENTO_MENSAGEM,
  carregarPreferencias,
  detectarEventos,
  entraramNoAguardando,
  eventosAudiveis,
  salvarPreferencias,
} from '@/conversas/notificacaoSom';

/**
 * O som do inbox: Web Audio puro, sem arquivo de áudio e sem biblioteca.
 *
 * Um .mp3 exigiria hospedar o arquivo, versioná-lo no build e lidar com a
 * primeira reprodução chegando depois do download. Dois osciladores resolvem o
 * mesmo problema em bytes zero e tocam no mesmo instante em que são pedidos.
 */

/**
 * Um `AudioContext` por aba, criado só quando alguém liga o som.
 *
 * Criar na importação do módulo seria criar em toda montagem do inbox —
 * navegador limita quantos existem ao mesmo tempo, e um contexto criado sem
 * gesto do usuário já nasce suspenso.
 */
let contexto = null;

function obterContexto() {
  try {
    const Construtor = globalThis.AudioContext ?? globalThis.webkitAudioContext;
    if (!Construtor) return null;
    if (!contexto) contexto = new Construtor();
    return contexto;
  } catch {
    // Navegador sem Web Audio, ou política que proíbe instanciar. Silêncio é a
    // degradação certa: o resto do inbox não pode cair por causa de um bipe.
    return null;
  }
}

/**
 * Destrava o áudio. **Só funciona dentro do clique do usuário.**
 *
 * O autoplay dos navegadores mantém o contexto `suspended` até um gesto real;
 * é por isso que o interruptor "Habilitar" chama isto, e não o efeito que
 * detecta os eventos — lá não há gesto nenhum, e o `resume()` seria ignorado.
 *
 * @returns `true` se o contexto está tocando de verdade.
 */
export async function destravarAudio() {
  const ctx = obterContexto();
  if (!ctx) return false;
  try {
    if (ctx.state === 'suspended') await ctx.resume();
    return ctx.state === 'running';
  } catch {
    return false;
  }
}

/**
 * Um bipe com envelope curto.
 *
 * O envelope não é capricho: ligar e desligar um oscilador na amplitude cheia
 * produz um estalo (a descontinuidade vira um clique no alto-falante). A rampa
 * exponencial nunca chega a zero — `exponentialRamp` não aceita zero —, daí o
 * 0.0001 nas pontas.
 */
function bipe(ctx, { frequencia, atraso, duracao, volume }) {
  const inicio = ctx.currentTime + atraso;
  const osc = ctx.createOscillator();
  const ganho = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.setValueAtTime(frequencia, inicio);

  ganho.gain.setValueAtTime(0.0001, inicio);
  ganho.gain.exponentialRampToValueAtTime(volume, inicio + 0.015);
  ganho.gain.exponentialRampToValueAtTime(0.0001, inicio + duracao);

  osc.connect(ganho);
  ganho.connect(ctx.destination);
  osc.start(inicio);
  osc.stop(inicio + duracao + 0.03);
}

/**
 * Timbres distintos por evento — o ponto é reconhecer SEM olhar para a tela.
 *
 * Aguardando são DOIS bipes subindo: "chegou alguém, ninguém pegou" é o que
 * exige ação de quem ouve. Mensagem é um bipe só, mais grave e mais curto:
 * acontece muito mais vezes ao longo do dia, e um som longo viraria tortura.
 */
/**
 * Quanto tempo uma conversa continua sendo "recém-chegada".
 *
 * Os três snapshots da chegada caíram em menos de um segundo no emulador; 5s dá
 * folga para a rede real sem começar a engolir mensagem legítima de quem acabou
 * de entrar na fila.
 */
const JANELA_DA_CHEGADA_MS = 5000;

const TIMBRES = {
  [EVENTO_AGUARDANDO]: [
    { frequencia: 880, atraso: 0, duracao: 0.11, volume: 0.13 },
    { frequencia: 1175, atraso: 0.14, duracao: 0.13, volume: 0.13 },
  ],
  [EVENTO_MENSAGEM]: [{ frequencia: 660, atraso: 0, duracao: 0.15, volume: 0.1 }],
};

/** Toca o som de um evento. Nunca lança — no pior caso não sai som. */
export function tocarSom(tipo) {
  const ctx = obterContexto();
  const notas = TIMBRES[tipo];
  if (!ctx || !notas) return false;
  try {
    // Contexto suspenso volta a tocar sozinho quando a aba readquire foco; o
    // `resume` aqui é oportunista e não espera resposta, porque este caminho
    // roda fora de gesto do usuário e pode simplesmente ser negado.
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    for (const nota of notas) bipe(ctx, nota);
    return true;
  } catch {
    return false;
  }
}

/**
 * Liga o som do inbox aos snapshots da lista de conversas.
 *
 * @param conversas  a lista AO VIVO do canal — não a filtrada. Filtrar aqui
 *                   faria a busca aberta na tela silenciar o inbox inteiro.
 * @param opcoes     `abertaId`, `uid` e `canalId`.
 */
export function useNotificacaoSom(conversas, { abertaId = null, uid = null, canalId = null } = {}) {
  const [preferencias, setPreferencias] = useState(() => carregarPreferencias(uid));
  const [bloqueado, setBloqueado] = useState(false);

  /**
   * O snapshot anterior e as preferências vivem em ref, não em estado.
   *
   * O snapshot porque compará-lo é efeito, não renderização: guardá-lo em
   * `useState` provocaria um render a cada mensagem que chega no inbox inteiro.
   * As preferências porque o efeito de detecção precisa do valor ATUAL sem
   * listar `preferencias` nas dependências — se listasse, mudar um toggle
   * reexecutaria a detecção sobre o mesmo par de snapshots.
   */
  const anteriorRef = useRef(null);
  const prefsRef = useRef(preferencias);

  /**
   * Quem entrou no aguardando há pouco: id → instante.
   *
   * Existe porque a chegada de um paciente atravessa TRÊS snapshots (ver
   * `detectarEventos`), e a comparação de dois em dois não enxerga isso. Sem
   * esta memória, cada paciente novo tocava dois sons diferentes.
   */
  const chegadasRef = useRef(new Map());

  // Troca de pessoa recarrega a preferência: o computador da recepção é
  // compartilhado, e o som de quem saiu não vale para quem entrou.
  useEffect(() => {
    const carregadas = carregarPreferencias(uid);
    prefsRef.current = carregadas;
    setPreferencias(carregadas);
  }, [uid]);

  /**
   * Baseline reiniciado ao trocar de canal ou de pessoa.
   *
   * Declarado ANTES do efeito de detecção de propósito: o React roda os efeitos
   * na ordem em que aparecem, então o zeramento acontece antes da comparação no
   * mesmo commit. Sem isso, entrar na Recepção vindo de Leads compararia duas
   * listas de conversas diferentes e tocaria uma rajada de sons por conversas
   * que só "apareceram" porque o canal mudou.
   */
  useEffect(() => {
    anteriorRef.current = null;
  }, [canalId, uid]);

  useEffect(() => {
    const anterior = anteriorRef.current;
    const atual = conversas ?? [];
    anteriorRef.current = atual;

    const agora = Date.now();
    const chegadas = chegadasRef.current;
    // A janela é curta de propósito: ela existe só para cobrir o intervalo
    // entre os passos da MESMA chegada. Esticá-la começaria a engolir mensagem
    // de verdade de quem acabou de entrar na fila.
    for (const [id, quando] of chegadas) {
      if (agora - quando > JANELA_DA_CHEGADA_MS) chegadas.delete(id);
    }
    // Registrar ANTES de detectar: quando os dois fatos caem no mesmo snapshot,
    // a supressão precisa já estar valendo nesta rodada.
    for (const id of entraramNoAguardando(anterior, atual)) chegadas.set(id, agora);

    const eventos = detectarEventos(anterior, atual, {
      abertaId,
      uid,
      chegadasRecentes: new Set(chegadas.keys()),
    });
    for (const tipo of eventosAudiveis(eventos, prefsRef.current)) tocarSom(tipo);
  }, [conversas, abertaId, uid]);

  const aplicar = useCallback(
    (proximas) => {
      prefsRef.current = proximas;
      setPreferencias(proximas);
      // Falha de gravação não vira erro na tela: a preferência vale para esta
      // sessão de qualquer forma, e o aviso aparece no painel.
      salvarPreferencias(uid, proximas);
    },
    [uid],
  );

  /** O interruptor geral. Ligar aqui é o gesto que destrava o áudio. */
  const alternarGeral = useCallback(async () => {
    const ligando = !prefsRef.current.geral;
    if (ligando) setBloqueado(!(await destravarAudio()));
    else setBloqueado(false);
    aplicar({ ...prefsRef.current, geral: ligando });
  }, [aplicar]);

  const alternarEvento = useCallback(
    (id) => {
      const eventos = { ...prefsRef.current.eventos, [id]: !prefsRef.current.eventos[id] };
      aplicar({ ...prefsRef.current, eventos });
    },
    [aplicar],
  );

  /**
   * "Tocar teste" também destrava, porque é clique de usuário.
   *
   * Quem abre o painel e clica em testar antes de habilitar merece ouvir o som
   * — senão concluiria que o recurso não funciona e nem chegaria a ligá-lo.
   */
  const tocarTeste = useCallback(async (tipo = EVENTO_AGUARDANDO) => {
    const liberado = await destravarAudio();
    setBloqueado(!liberado);
    return tocarSom(tipo);
  }, []);

  return { preferencias, bloqueado, alternarGeral, alternarEvento, tocarTeste };
}

export { EVENTO_AGUARDANDO, EVENTO_MENSAGEM };
